import cn from 'classnames';
import styles from './InputFile.module.scss';
import { useController, useFormContext } from 'react-hook-form';
import { ChangeEvent, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

type InputFileProps = {
  label?: string;
  name: string;
  className?: string;
  disabled?: boolean;
  multiple?: boolean;
  max?: number;
};

export function InputFile({
  label,
  name,
  className,
  disabled,
  multiple,
  max,
}: InputFileProps) {
  const { control } = useFormContext();
  const { field } = useController({
    name,
    control,
  });

  const [previews, setPreviews] = useState<string[]>([]);

  const wrapperClassName = cn(styles.wrapper, className);
  const containerClassName = cn(styles.container, {
    [styles.disabled]: disabled,
  });

  useEffect(() => {
    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview));
    };
  }, [previews]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      field.onChange([]);
      return;
    }

    const files = Array.from(event.target.files);
    if (multiple) {
      if (max && files.length > max) {
        toast.error(`Вы можете добавить не более ${max} файлов`);
        return;
      }
    }
    field.onChange(files);

    if (multiple) {
      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setPreviews((prev) => {
        prev.forEach((preview) => URL.revokeObjectURL(preview));
        return newPreviews;
      });
    } else {
      const newPreviews = files
        .map((file) => URL.createObjectURL(file))
        .slice(0, 1);
      setPreviews((prev) => {
        prev.forEach((preview) => URL.revokeObjectURL(preview));
        return newPreviews;
      });
    }
  };

  const handleDelete = (previewToDelete: string, indexToDelete: number) => {
    URL.revokeObjectURL(previewToDelete);
    setPreviews((prev) => prev.filter((item) => item !== previewToDelete));

    const currentFiles = Array.isArray(field.value)
      ? (field.value as File[])
      : [];
    const updatedFiles = currentFiles.filter(
      (_, index) => index !== indexToDelete,
    );
    field.onChange(updatedFiles);
  };

  return (
    <div className={wrapperClassName}>
      <div className={containerClassName}>
        <label className={styles.addButton}>
          <img
            src="/svg/add-image.svg"
            alt="add-image"
            className={styles.icon}
          />
          {previews.length === 0 && (
            <span className={styles.text}>{label}</span>
          )}
          <input
            type="file"
            accept="image/*"
            multiple={multiple}
            onChange={handleChange}
            className={styles.input}
            disabled={disabled}
            max={max}
          />
        </label>
        {previews.map((preview, index) => (
          <div className={styles.preview} key={preview}>
            <img
              src={preview}
              alt={`preview-${index}`}
              className={styles.icon}
            />
            <div
              className={styles.close}
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(preview, index);
              }}
            >
              <img
                src="/svg/close.svg"
                alt="close"
                className={styles.closeIcon}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
